import React from "react";
import { useParams } from "react-router-dom";
import { denormalize } from "normalizr";

import { useTheme } from "@mui/material/styles";
import { least } from "d3-array";

import Box from "@mui/material/Box";
import { CatalogStore, FilterStore, ConfigurationStore } from "stores";
import { vehicleInListSchema, vehicleSchema } from "schemas";
import { TVehicle } from "types";
import {
  getCustomerType,
  getOffersFromVehicle,
  getValidOffersFromVehicle,
  normalizeType,
} from "helper/finance_helper";

import { calculateMaxDownPayment } from "./components/Finance/calculateMaxDownPayment";
import { tile } from "./components/Finance/Finance";
import { json2normalized } from "fixtures/helper";
import { isNeuwagen } from "components/EnVKV/helper";
import { HistoryLogger } from "containers";

import {
  Backdrop,
  Contact,
  Dealer,
  Dealers,
  Emission,
  Features,
  Finance,
  Gallery,
  AdditionalOptions,
  Options,
  Summary,
} from "./components";

import Header from "./Header";
import { checkQuality } from "components/EnVKV";

import { useSmartNavigate } from "helper/SmartLink";

export const DefaultFinanceFilter = {
  type: "buy",
  customerType: "private",
};

const Detail: React.FC = (): JSX.Element | null => {
  const [financeFilter, setFinanceFilter] =
    React.useState<any>(DefaultFinanceFilter);

  const [financeRate, setFinanceRate] = React.useState<any>();
  const [detail, setDetail] = React.useState<TVehicle>();
  const [vehicle, setVehicle] = React.useState<TVehicle>();
  const [locationId, setLocationId] = React.useState<number>();

  const {
    breakpoints: { values: breakpoints },
  } = useTheme();

  const configuration = React.useContext(ConfigurationStore);

  const {
    state: { entities },
  } = React.useContext(CatalogStore);

  const {
    state: { filter: searchFilter },
  } = React.useContext(FilterStore);
  const { finance: searchFinanceFilter = null } = searchFilter;

  const navigate = useSmartNavigate();

  const { id } = useParams();

  // Pull vehicle out of the store
  React.useEffect(() => {
    if (!entities?.vehicle) return;
    const vehicle: TVehicle = denormalize(id, vehicleInListSchema, entities);
    if (!vehicle) {
      navigate("/catalog/ar/" + id, { replace: true });
      return;
    }
    setVehicle(vehicle);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entities, id]);

  // Fetch detailed data
  React.useEffect(() => {
    const dataFetch = async () => {
      const data = await (
        await fetch(
          `${process.env.REACT_APP_API_URL}/detail/${configuration.showroomId || process.env.REACT_APP_SHOP_ID}/${id}`,
        )
      ).json();
      const { vehicle, ...rest } = data;
      const { entities } = json2normalized(rest);
      const detail = denormalize(vehicle, vehicleSchema, entities);
      setDetail(detail);
    };

    dataFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  React.useEffect(() => {
    if (vehicle?.locations && vehicle.locations.length > 0) {
      let result;
      if (searchFilter?.location && searchFilter?.location.length > 0) {
        result = parseInt(searchFilter?.location[0]);
      } else {
        result = vehicle.locations[0].id;
      }
      setLocation(result);
    }
  }, [vehicle, searchFilter]);

  const setLocation = (id: any) => {
    setLocationId(parseInt(id));
  };

  // Set the initial finance filter for the vehicle. Either use the global one from the catalog, or if following a deep link create one from the available rates.
  React.useEffect(() => {
    if (!vehicle) return;
    let newFilter: any = {
      type: "buy",
      customerType: "private",
      leasing: true,
      balloon: true,
      credit: true,
    };

    if (searchFinanceFilter) {
      const { type: filterFinanceType, deposit = 0 }: any = searchFinanceFilter;

      if (filterFinanceType === "buy") {
        setFinanceRate({ type: "buy" });
        setFinanceFilter(searchFinanceFilter);
        return;
      }

      const offers = getValidOffersFromVehicle(vehicle, searchFinanceFilter);
      const bestRate = least(offers, (rate: any) => rate.rate);

      if (bestRate) {
        newFilter = {
          ...newFilter,
          deposit,
          customerType: getCustomerType(bestRate.type),
          type: bestRate.type ? normalizeType(bestRate.type) : null,
          mileage: bestRate.mileage || null,
          runTime: bestRate.runtime || null,
          leasing: bestRate.financeType === "leasing",
          balloon: bestRate.financeType === "balloon",
          credit: bestRate.financeType === "credit",
        };
      }
    } else {
      // Kein Filter vorgegeben -> Filter an bester Rate ausrichten
      const offers = getOffersFromVehicle(vehicle);
      const bestRate = least(offers, (rate: any) => rate.rate);

      if (bestRate) {
        newFilter = {
          ...newFilter,
          deposit: 0,
          customerType: getCustomerType(bestRate.type),
          type: bestRate.type ? normalizeType(bestRate.type) : null,
          mileage: bestRate.mileage || null,
          runTime: bestRate.runtime || null,
          leasing: bestRate.financeType === "leasing",
          balloon: bestRate.financeType === "balloon",
          credit: bestRate.financeType === "credit",
        };
      }
    }

    setFinanceFilter(newFilter);
    return;
  }, [vehicle, searchFinanceFilter, id]);

  // Filter changed, Calculate best Rate
  React.useEffect(() => {
    if (!(vehicle && financeFilter && detail && vehicle.wgnr === detail.wgnr)) {
      return;
    }
    if (financeFilter.type === "buy") {
      setFinanceRate({ type: "buy", customerType: financeFilter.customerType });
      return;
    }

    const newFinanceFilter = {
      ...financeFilter,
      leasing: financeFilter.type === "leasing",
      balloon: financeFilter.type === "balloon",
      credit: financeFilter.type === "credit",
    };

    const offers = getValidOffersFromVehicle(
      detail || vehicle,
      newFinanceFilter,
    );

    if (offers.length === 0) {
      setFinanceRate({ type: "buy", customerType: financeFilter.customerType });
      return;
    }
    // else {
    //   const currentFinanceRateStillAvailable = offers.find(
    //     (offer) => offer.id === financeRate?.id,
    //   );
    //   if (currentFinanceRateStillAvailable) {
    //     console.log(
    //       "Current finance rate still available",
    //       currentFinanceRateStillAvailable,
    //     );
    //     setFinanceRate({
    //       ...currentFinanceRateStillAvailable,
    //       maxDeposit: financeRate?.maxDeposit,
    //     });
    //     return;
    //   }
    // }
    const bestRate = least(offers, (rate) => rate.rate);

    if (!bestRate) return;

    const minimalRate: number = process.env.REACT_APP_MINIMUM_RATE
      ? parseInt(process.env.REACT_APP_MINIMUM_RATE)
      : 0;

    let maxDownPayment = 2000;
    try {
      maxDownPayment = calculateMaxDownPayment(
        bestRate,
        bestRate.runtime,
        minimalRate,
      );
    } catch (e) {
      console.error(e);
    }

    const tiler = tile(maxDownPayment);
    const roundedMaxDownPayment = Math.floor(maxDownPayment / tiler) * tiler;
    bestRate.maxDeposit = roundedMaxDownPayment;

    if (roundedMaxDownPayment < financeFilter.deposit) {
      setFinanceFilter({ ...newFinanceFilter, deposit: roundedMaxDownPayment });
      return;
    }
    setFinanceRate({
      ...bestRate,
      deposit: newFinanceFilter.deposit,
    });
  }, [financeFilter, detail, vehicle]);

  // Move up the page
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [vehicle]);

  React.useEffect(
    () => console.log("Finance Rate", financeRate),
    [financeRate],
  );

  // React.useEffect(
  //   () => console.log("Finance Filter", financeFilter),
  //   [financeFilter],
  // );

  if (!vehicle) return null;

  const carIsNew = isNeuwagen(vehicle);
  const quality = checkQuality(vehicle);

  return (
    <>
      <HistoryLogger id={vehicle.wgnr} />
      <Header vehicle={vehicle} />
      <Backdrop vehicle={detail || vehicle} />
      <Box
        sx={{
          display: "grid",
          columnGap: 2,
          rowGap: 2,
          gridTemplateColumns: "repeat(6, 1fr)",
        }}
        vocab="https://schema.org/"
        typeof="Car"
      >
        <Gallery
          vehicle={detail || vehicle}
          paperProps={{
            variant: "outlined",
            sx: {
              p: 1,
              height: { xs: 250, md: 350 },
              overflow: "hidden",
              gridColumn: "span 6",
            },
          }}
          galleryProps={{
            spaceBetween: 2,
            breakpoints: {
              0: {
                slidesPerView: 1,
              },
              [breakpoints.sm]: {
                slidesPerView: 2,
              },
              [breakpoints.lg]: {
                slidesPerView: 3,
              },
            },
          }}
        />
        <Summary
          {...{
            quality,
            vehicle,
            financeRate,
            financeFilter,
          }}
          paperProps={{
            variant: "outlined",

            sx: {
              p: 2,
              gridColumn: { xs: "span 6", sm: "span 3", md: "span 2" },
            },
          }}
        />
        <Features
          vehicle={vehicle}
          paperProps={{
            variant: "outlined",

            sx: {
              p: 2,
              gridColumn: { xs: "span 6", sm: "span 3", md: "span 4" },
            },
          }}
        />

        {carIsNew && (
          <Emission
            paperProps={{
              sx: {
                p: 2,
                gridColumn: "span 6",
              },
            }}
            vehicle={vehicle}
          />
        )}
        <Finance
          paperProps={{
            sx: { p: 2, gridColumn: { xs: "span 6" } },
          }}
          {...{
            vehicle,
            filter: financeFilter,
            setFilter: setFinanceFilter,
            financeRate,
          }}
        />
        <Options
          paperProps={{
            sx: {
              p: 2,
              gridColumn: { xs: "span 6" },
            },
          }}
          vehicle={vehicle}
        />
        <AdditionalOptions
          paperProps={{
            sx: {
              p: 2,
              gridColumn: { xs: "span 6" },
            },
          }}
          vehicle={detail || vehicle}
        />
        {vehicle.location && (
          <Dealer
            paperProps={{
              sx: { p: 2, gridColumn: { xs: "span 6", lg: "span 3" } },
            }}
            vehicle={vehicle}
          />
        )}
        {vehicle.locations && vehicle.locations.length > 0 && (
          <Dealers
            paperProps={{
              sx: { p: 2, gridColumn: { xs: "span 6", lg: "span 3" } },
            }}
            vehicle={vehicle}
            locationId={locationId}
            setLocationHandler={setLocation}
          />
        )}
        <Contact
          paperProps={{
            sx: { p: 2, gridColumn: { xs: "span 6", lg: "span 3" } },
          }}
          vehicle={vehicle}
          configuration={configuration}
          locationId={locationId}
          setLocationHandler={vehicle.location ? undefined : setLocation}
        />
      </Box>
    </>
  );
};

export default Detail;
